import { useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Hook para gestionar el análisis Pando de una oferta con polling asíncrono.
 *
 * Encapsula:
 *   - estado `aiAnalysis` (documento con resumen_ejecutivo, conclusion, etc.)
 *   - flag `analyzingAi`
 *   - función `runAiAnalysis(modelOverride?)` que lanza el job (HTTP 202)
 *     y hace polling cada 3.5s hasta done/failed (max 5 min).
 *
 * @param {string} offerId — id de la oferta
 * @param {{
 *   aiModel: string,
 *   includeInternalHours: boolean,
 *   onOfferRefresh?: (fullOffer) => void,
 *   onSectionsRefresh?: (sections) => void,
 * }} opts — modelo, flag horas internas y callbacks al refrescar.
 */
export function useOfferAiAnalysis(offerId, opts) {
  const {
    aiModel = "claude-sonnet-4-6",
    includeInternalHours = true,
    onOfferRefresh,
    onSectionsRefresh,
  } = opts || {};

  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingAi, setAnalyzingAi] = useState(false);

  const runAiAnalysis = useCallback(async (modelOverride) => {
    setAnalyzingAi(true);
    try {
      const model = modelOverride || aiModel || "claude-sonnet-4-6";
      // 1) Lanza el job (HTTP 202)
      const { data: launch } = await axios.post(
        `${API}/offers/${offerId}/ai-analysis`,
        null,
        { params: { model, add_internal_hours: includeInternalHours }, timeout: 30000 }
      );
      if (!launch?.job_id) throw new Error("No se recibió job_id");

      // 2) Poll hasta done/failed (max 5 min)
      const start = Date.now();
      const MAX_MS = 5 * 60 * 1000;
      while (Date.now() - start < MAX_MS) {
        await new Promise(r => setTimeout(r, 3500));
        const { data: st } = await axios.get(
          `${API}/offers/${offerId}/ai-analysis/status`,
          { timeout: 15000 }
        );
        if (st.status === "done") {
          setAiAnalysis(st.analysis);
          if (st.cost_sections) {
            // Refrescar oferta completa para reflejar las horas internas IT
            const { data: refreshed } = await axios.get(`${API}/offers/${offerId}`);
            if (typeof onOfferRefresh === "function")   onOfferRefresh(refreshed);
            if (typeof onSectionsRefresh === "function") onSectionsRefresh(refreshed.cost_sections || []);
          }
          return st.analysis;
        }
        if (st.status === "failed") {
          throw new Error(st.error || "Análisis Pando falló");
        }
      }
      throw new Error("Timeout esperando análisis Pando (>5 min)");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Error generando análisis Pando";
      toast.error("Error generando análisis Pando", { description: msg });
      throw e;
    } finally {
      setAnalyzingAi(false);
    }
  }, [offerId, aiModel, includeInternalHours, onOfferRefresh, onSectionsRefresh]);

  return { aiAnalysis, setAiAnalysis, analyzingAi, runAiAnalysis };
}
